import React, { useEffect, useState, useCallback } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchAllSelectorExperiences,
  fetchSelectorExperiencesForOrganization,
  createSelectorExperience,
} from "../../redux/actions/selectorExperienceActions";
import { fetchOrganizations } from "../../redux/actions/organizationActions";
import { RootState, AppDispatch } from "../../redux/store";
import { getOrganizationDisplayName } from "../../utils/render";
import Fuse from "fuse.js";
import CreateSelectorExperienceDialog from "./selector-experiences/CreateSelectorExperienceDialog";

interface SelectorExperiencesTabProps {
  organizationId?: string;
}

const SelectorExperiencesTab: React.FC<SelectorExperiencesTabProps> = ({
  organizationId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { selectorExperiences, loading, error } = useSelector(
    (state: RootState) => state.selectorExperiences
  );
  const { organizations, loading: orgsLoading } = useSelector(
    (state: RootState) => state.organizations
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Fetch selector experiences and organizations when the component mounts
  useEffect(() => {
    if (organizationId) {
      dispatch(fetchSelectorExperiencesForOrganization(organizationId));
    } else {
      dispatch(fetchAllSelectorExperiences());
    }
    // Only fetch organizations if we're not already in an organization context
    if (!organizationId) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, organizationId]);

  const handleViewSelectorExperience = useCallback(
    (id: string, orgId: string) => {
      navigate(`/dashboard/selector-experiences/${orgId}/${id}`);
    },
    [navigate]
  );

  const handleCreateSelectorExperience = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCreateSelectorExperienceSubmit = (data: any) => {
    dispatch(createSelectorExperience(data))
      .unwrap()
      .then((selector) => {
        toast.success("Selector Experience created successfully");
        handleDialogClose();
        navigate(
          `/dashboard/selector-experiences/${selector.organization_id}/${selector.selector_id}`,
          {
            state: { toastMessage: "Selector Experience created successfully" },
          }
        );
      })
      .catch((error: any) => {
        if (error.status === 403) {
          toast.error("You don't have permission to create selector experiences for this organization");
        } else {
          toast.error(`Failed to create selector experience: ${error.message || 'Unknown error'}`);
        }
      });
  };

  // Filter selector experiences by organization if organizationId is provided
  let filteredSelectorExperiences = organizationId
    ? selectorExperiences.filter((se) => se.organization_id === organizationId)
    : selectorExperiences;

  // Apply search filter
  if (searchQuery) {
    const fuse = new Fuse(filteredSelectorExperiences, {
      keys: ["selector_id", "selector_title"],
      threshold: 0.3,
    });
    filteredSelectorExperiences = fuse
      .search(searchQuery)
      .map((result) => result.item);
  }

  const columns: GridColDef[] = [
    {
      field: "selector_id",
      headerName: "Selector ID",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "selector_title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => params || "-",
    },
    {
      field: "entries",
      headerName: "# Entries",
      flex: 0.5,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => params?.length || 0,
    },
    {
      field: "organization_id",
      headerName: "Organization",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      valueGetter: (params: any) => {
        if (!params) return "None";
        const org = organizations.find((o) => o.id === params);
        return org ? getOrganizationDisplayName(org) : params;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      disableColumnMenu: true,
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            handleViewSelectorExperience(
              params.row.selector_id,
              params.row.organization_id
            )
          }
        >
          View
        </Button>
      ),
      headerAlign: "center",
      align: "center",
    },
  ];

  if (loading && selectorExperiences.length === 0) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 3,
        }}
      >
        <TextField
          label="Search Selector Experiences"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by selector ID or title"
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateSelectorExperience}
          size="medium"
          sx={{ fontSize: "0.9rem", padding: "6px 16px" }}
        >
          Create Selector Experience
        </Button>
      </Box>

      <Box sx={{ height: organizationId ? "40vh" : "70vh", width: "100%" }}>
        <DataGrid
          rows={filteredSelectorExperiences}
          columns={columns}
          getRowId={(row) => row.selector_id}
          initialState={{
            sorting: {
              sortModel: [{ field: "created_at", sort: "desc" }],
            },
          }}
        />
      </Box>

      {/* Enhanced Create Dialog */}
      <CreateSelectorExperienceDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleCreateSelectorExperienceSubmit}
        organizations={organizations}
        orgsLoading={orgsLoading}
        preselectedOrgId={organizationId}
      />
    </>
  );
};

export default SelectorExperiencesTab;
